import React, { useMemo, useState } from 'react';
import PortfolioHistoryChart from '../../components/Charts/PortfolioHistoryChart';

interface HistoryPoint {
    date: string;
    total_value: number;
    cash: number;
    positions_value: number;
}

interface NiaPortfolioHistoryProps {
    history: HistoryPoint[];
    totalInvested: number;
    isMobile?: boolean;
}

type PeriodKey = '5J' | '1M' | '3M' | '6M' | '1A';

// `seances` : nombre de jours de bourse (lun-ven) ; `jours` : jours calendaires.
const PERIODS: { key: PeriodKey; label: string; seances?: number; jours?: number }[] = [
    { key: '5J', label: '5 J', seances: 5 },
    { key: '1M', label: '1 M', jours: 30 },
    { key: '3M', label: '3 M', jours: 90 },
    { key: '6M', label: '6 M', jours: 180 },
    { key: '1A', label: '1 an', jours: 365 },
];

/** Plus grande fenêtre proposée : c'est celle que l'écran doit charger. */
export const NIA_HISTORY_MAX_DAYS = 365;

const isWeekday = (isoDate: string) => {
    const day = new Date(isoDate).getDay();
    return day >= 1 && day <= 5;
};

const NiaPortfolioHistory: React.FC<NiaPortfolioHistoryProps> = ({ history, totalInvested, isMobile = false }) => {
    const [period, setPeriod] = useState<PeriodKey>('3M');

    const visibleHistory = useMemo(() => {
        if (!history || history.length === 0) return [];
        const selected = PERIODS.find(p => p.key === period) || PERIODS[2];
        if (selected.seances) {
            return history.filter(d => isWeekday(d.date)).slice(-selected.seances);
        }
        const cutoff = new Date(history[history.length - 1].date);
        cutoff.setDate(cutoff.getDate() - (selected.jours || 0));
        const cutoffIso = cutoff.toISOString().slice(0, 10);
        return history.filter(d => d.date >= cutoffIso);
    }, [history, period]);

    const seances = visibleHistory.filter(d => isWeekday(d.date)).length;

    const selector = (
        <div style={{ display: 'flex', background: 'var(--bg-input)', borderRadius: 10, padding: 3, gap: 2 }}>
            {PERIODS.map(p => {
                const active = p.key === period;
                return (
                    <button
                        key={p.key}
                        onClick={() => setPeriod(p.key)}
                        aria-pressed={active}
                        style={{
                            flex: isMobile ? 1 : undefined,
                            padding: isMobile ? '8px 0' : '6px 14px',
                            borderRadius: 8,
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            background: active ? '#7c3aed' : 'transparent',
                            color: active ? '#ffffff' : 'var(--text-muted)',
                            transition: 'all 0.2s',
                            touchAction: 'manipulation',
                        }}
                    >
                        {p.label}
                    </button>
                );
            })}
        </div>
    );

    if (isMobile) {
        return (
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '12px' }}>
                    📈 Évolution
                </div>
                <div style={{ marginBottom: 12 }}>{selector}</div>
                <PortfolioHistoryChart history={visibleHistory} totalInvested={totalInvested} isMobile={true} />
            </div>
        );
    }

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                <div>
                    <h4 style={{ fontSize: 18, fontWeight: '700', color: 'var(--text-heading)', margin: 0 }}>📈 Évolution du Portefeuille</h4>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {seances} séances · Ligne pointillée = capital investi
                    </span>
                </div>
                {selector}
            </div>
            <PortfolioHistoryChart history={visibleHistory} totalInvested={totalInvested} />
        </>
    );
};

export default NiaPortfolioHistory;
